/**
 * LiveLayerCard Component
 *
 * Card for displaying and toggling live layers (React-managed, not from database).
 * Simpler than LayerCard since live layers don't support opacity or reordering.
 */

import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/free-solid-svg-icons';
import './LiveLayerCard.scss';
import { LiveLayerDef, useLayerDrawer } from '../LayerDrawerContext';

interface LiveLayerCardProps {
  liveLayer: LiveLayerDef;
  onToggle: () => void;
}

export const LiveLayerCard: React.FC<LiveLayerCardProps> = ({
  liveLayer,
  onToggle
}) => {
  const { selectionMode, selectedLayerIds, toggleLayerSelection } = useLayerDrawer();
  const [isToggling, setIsToggling] = useState(false);

  const isSelected = selectedLayerIds.has(liveLayer.id);

  /**
   * Handle layer toggle
   */
  const handleToggle = useCallback(async () => {
    setIsToggling(true);
    try {
      onToggle();
    } finally {
      // Small delay for visual feedback
      setTimeout(() => setIsToggling(false), 200);
    }
  }, [onToggle]);

  /**
   * Handle selection checkbox change
   */
  const handleSelectionChange = useCallback(() => {
    toggleLayerSelection(liveLayer.id);
  }, [liveLayer.id, toggleLayerSelection]);

  // Parse the icon class (e.g., "fa-solid fa-cloud-bolt" -> icon object)
  const iconClass = liveLayer.icon || 'fa-solid fa-layer-group';

  return (
    <div
      className={`live-layer-card ${liveLayer.enabled ? 'live-layer-card--enabled' : ''} ${isSelected ? 'live-layer-card--selected' : ''} ${selectionMode ? 'live-layer-card--selection-mode' : ''}`}
      data-layer-id={liveLayer.id}
    >
      <div className="live-layer-card-content">
        {/* Selection checkbox - shown in selection mode */}
        {selectionMode && (
          <label className="live-layer-card-selection-checkbox">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={handleSelectionChange}
              aria-label={`Select ${liveLayer.name}`}
            />
            <span className="checkbox-custom" />
          </label>
        )}
        {/* Live indicator */}
        <div className="live-layer-card-live-badge" title="Real-time data">
          <FontAwesomeIcon icon={faBolt} />
        </div>

        {/* Icon */}
        <div className="live-layer-card-icon">
          <i className={iconClass}></i>
        </div>

        {/* Name and description */}
        <div className="live-layer-card-info">
          <div className="live-layer-card-name">{liveLayer.name}</div>
          {liveLayer.description && (
            <div className="live-layer-card-description">{liveLayer.description}</div>
          )}
        </div>

        {/* Toggle */}
        <button
          className={`live-layer-card-toggle ${liveLayer.enabled ? 'live-layer-card-toggle--on' : ''}`}
          onClick={handleToggle}
          disabled={isToggling}
          aria-label={`Toggle ${liveLayer.name} ${liveLayer.enabled ? 'off' : 'on'}`}
          title={liveLayer.enabled ? 'Click to hide' : 'Click to show'}
        >
          <span className="toggle-track">
            <span className="toggle-thumb" />
          </span>
        </button>
      </div>
    </div>
  );
};
